import React, { useState, useEffect } from 'react';
import { settingsAPI, locationsAPI } from '../../../services/api';
import ImageUploader from '../Shared/ImageUploader';
import '../Shared/FormStyles.scss';

const SettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchLocations();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (err) {
      setError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationsAPI.getAllAdmin();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch locations', err);
    }
  };

  const getSelectedLocationMapUrl = () => {
    const selectedId = settings?.contact?.selectedLocationId;
    if (!selectedId) return null;
    // selectedLocationId may be a populated object or just an ID string
    if (typeof selectedId === 'object' && selectedId.mapEmbedUrl) {
      return selectedId.mapEmbedUrl;
    }
    const loc = locations.find((l) => l._id === selectedId);
    return loc?.mapEmbedUrl || null;
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    try {
      await settingsAPI.update(settings);
      setSuccess('Settings updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update settings');
    }
  };

  const handleChange = (path, value) => {
    const keys = path.split('.');
    setSettings((prev) => {
      const newSettings = { ...prev };
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const handleLogoChange = async (type, url) => {
    try {
      await settingsAPI.updateLogo(type, url);
      handleChange(type === 'header' ? 'headerLogo' : type === 'footer' ? 'footerLogo' : 'favicon', url);
      setSuccess('Logo updated successfully');
    } catch (err) {
      setError('Failed to update logo');
    }
  };

  const handleHourChange = (index, field, value) => {
    const newHours = [...(settings.openingHours || [])];
    newHours[index] = { ...newHours[index], [field]: value };
    handleChange('openingHours', newHours);
  };

  if (loading || !settings) return <div>Loading...</div>;

  const defaultDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const hours = settings.openingHours || defaultDays.map(day => ({ day, hours: '', isClosed: false }));

  return (
    <div className="settings-manager">
      <div className="admin-form">
        <h2>Site Settings</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <h3>Logos</h3>
        <ImageUploader
          value={settings.headerLogo}
          onChange={(url) => handleLogoChange('header', url)}
          label="Header Logo"
        />
        <ImageUploader
          value={settings.footerLogo}
          onChange={(url) => handleLogoChange('footer', url)}
          label="Footer Logo"
        />
        <ImageUploader
          value={settings.favicon}
          onChange={(url) => handleLogoChange('favicon', url)}
          label="Favicon"
        />

        <h3>Contact Information</h3>
        <div className="form-group">
          <label>Phone</label>
          <input
            type="text"
            value={settings.contact?.phone || ''}
            onChange={(e) => handleChange('contact.phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={settings.contact?.email || ''}
            onChange={(e) => handleChange('contact.email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Street Address</label>
          <input
            type="text"
            value={settings.contact?.address?.street || ''}
            onChange={(e) => handleChange('contact.address.street', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>City/State</label>
          <input
            type="text"
            value={settings.contact?.address?.city || ''}
            onChange={(e) => handleChange('contact.address.city', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Map Location</label>
          <select
            value={
              typeof settings.contact?.selectedLocationId === 'object'
                ? settings.contact?.selectedLocationId?._id || ''
                : settings.contact?.selectedLocationId || ''
            }
            onChange={(e) => handleChange('contact.selectedLocationId', e.target.value || null)}
          >
            <option value="">-- Select a location --</option>
            {locations.map((loc) => (
              <option key={loc._id} value={loc._id}>
                {loc.name}
              </option>
            ))}
          </select>
          {getSelectedLocationMapUrl() && (
            <div style={{ marginTop: '10px' }}>
              <iframe
                src={getSelectedLocationMapUrl()}
                width="100%"
                height="250"
                style={{ border: 0, borderRadius: '8px' }}
                allowFullScreen=""
                loading="lazy"
                title="Map Preview"
              ></iframe>
            </div>
          )}
        </div>

        {/* <h3>Opening Hours</h3>
        {hours.map((hour, index) => (
          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={hour.day}
              readOnly
              style={{ width: '120px' }}
            />
            <label>
              <input
                type="checkbox"
                checked={hour.isClosed}
                onChange={(e) => handleHourChange(index, 'isClosed', e.target.checked)}
              />
              Closed
            </label>
            <input
              type="text"
              value={hour.hours}
              onChange={(e) => handleHourChange(index, 'hours', e.target.value)}
              placeholder="e.g., 8am - 7pm"
              disabled={hour.isClosed}
              style={{ flex: 1 }}
            />
          </div>
        ))} */}

        <h3>Social Media</h3>
        <div className="form-group">
          <label>Facebook URL</label>
          <input
            type="url"
            value={settings.socialMedia?.facebook || ''}
            onChange={(e) => handleChange('socialMedia.facebook', e.target.value)}
            placeholder="https://facebook.com/yourpage"
          />
        </div>
        <div className="form-group">
          <label>Instagram URL</label>
          <input
            type="url"
            value={settings.socialMedia?.instagram || ''}
            onChange={(e) => handleChange('socialMedia.instagram', e.target.value)}
            placeholder="https://instagram.com/yourprofile"
          />
        </div>

        <h3>Footer</h3>
        <div className="form-group">
          <label>Copyright Text</label>
          <input
            type="text"
            value={settings.footer?.copyrightText || ''}
            onChange={(e) => handleChange('footer.copyrightText', e.target.value)}
          />
        </div>

        <h3>Page Hero Background Images</h3>
        <p style={{ fontSize: '13px', color: '#888', marginTop: '-8px', marginBottom: '20px' }}>
          Each page below gets its own parallax background image behind the title. Leave a page empty to use the solid colour instead.
        </p>
        {[
          { key: 'services', label: 'Services Page' },
          { key: 'conditions', label: 'Conditions Page' },
          { key: 'fees', label: 'Fees Page' },
          { key: 'ourTeam', label: 'Our Team Page' },
          { key: 'faq', label: 'FAQ Page' },
          { key: 'workWithUs', label: 'Work With Us Page' },
          { key: 'contact', label: 'Contact Page' },
          { key: 'blog', label: 'Blog Page' },
        ].map(({ key, label }) => (
          <ImageUploader
            key={key}
            value={settings.pageHeroImages?.[key]}
            onChange={(url) => handleChange(`pageHeroImages.${key}`, url)}
            label={label}
          />
        ))}

        <div className="form-group">
          <label>Hero Section Background Color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="color"
              value={settings.pageHeroColor || '#464e65'}
              onChange={(e) => handleChange('pageHeroColor', e.target.value)}
              style={{ width: '48px', height: '36px', padding: '2px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            <input
              type="text"
              value={settings.pageHeroColor || '#464e65'}
              onChange={(e) => handleChange('pageHeroColor', e.target.value)}
              placeholder="#464e65"
              style={{ width: '120px' }}
            />
            <span style={{ fontSize: '13px', color: '#888' }}>Applied to all page hero sections</span>
          </div>
        </div>

        <h3>Page "About" Sections</h3>
        <p style={{ fontSize: '13px', color: '#888', marginTop: '-8px', marginBottom: '20px' }}>
          Each page below can show an "About" section (heading, paragraph and a parallax blob image) right under its page title. Leave a page's fields empty to hide the section on that page.
        </p>
        {[
          { key: 'services', label: 'Services Page' },
          { key: 'conditions', label: 'Conditions Page' },
          { key: 'fees', label: 'Fees Page' },
          { key: 'ourTeam', label: 'Our Team Page' },
          { key: 'faq', label: 'FAQ Page' },
          { key: 'workWithUs', label: 'Work With Us Page' },
          { key: 'contact', label: 'Contact Page' },
          { key: 'blog', label: 'Blog Page' },
        ].map(({ key, label }) => (
          <div key={key} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
            <strong>{label}</strong>
            <div className="form-group">
              <label>Heading</label>
              <input
                type="text"
                value={settings.pageAboutSections?.[key]?.heading || ''}
                onChange={(e) => handleChange(`pageAboutSections.${key}.heading`, e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Paragraph</label>
              <textarea
                value={settings.pageAboutSections?.[key]?.paragraph || ''}
                onChange={(e) => handleChange(`pageAboutSections.${key}.paragraph`, e.target.value)}
                rows={3}
              />
            </div>
            <ImageUploader
              value={settings.pageAboutSections?.[key]?.image}
              onChange={(url) => handleChange(`pageAboutSections.${key}.image`, url)}
              label="Blob Image"
            />
          </div>
        ))}

        <h3>SEO</h3>
        <div className="form-group">
          <label>Site Title</label>
          <input
            type="text"
            value={settings.seo?.siteTitle || ''}
            onChange={(e) => handleChange('seo.siteTitle', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Site Description</label>
          <textarea
            value={settings.seo?.siteDescription || ''}
            onChange={(e) => handleChange('seo.siteDescription', e.target.value)}
            rows={3}
          />
        </div>

        <div className="form-actions">
          <button type="button" className="dashboard-btn btn-primary" onClick={handleSave}>
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsManager;
